import React, { useMemo } from 'react'

import { t } from 'src/translations'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

import { formatLocationAddress } from 'public/components/online_ordering/addressUtils'
import { Location as LocationType } from 'public/components/online_ordering/types'

import { resources } from 'config'

type Props = {
  restaurantGuid: string
  restaurantName: string
  location: LocationType
}

const Location = ({ restaurantGuid, restaurantName, location }: Props) => {
  const tracker = useTracker()
  const formattedAddress = useMemo(
    () =>
      location ? `${restaurantName}, ${formatLocationAddress(location)}` : null,
    [location, restaurantName]
  )

  const embeddedMapURL = useMemo(() => {
    if (formattedAddress) {
      const params = new URLSearchParams({ address: formattedAddress })
      return `${resources.mapEndpoint}/map?${params.toString()}`
    }
    if (location.longitude && location.latitude) {
      const params = new URLSearchParams({
        address: `${location.latitude},${location.longitude}`
      })
      return `${resources.mapEndpoint}/map?${params.toString()}`
    }
    return null
  }, [formattedAddress, location.latitude, location.longitude])
  const directionsURL = useMemo(
    () =>
      formattedAddress
        ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
            formattedAddress
          )}}`
        : '',
    [formattedAddress]
  )

  return (
    <div className='location-container'>
      <p>{formatLocationAddress(location)}</p>
      <p>
        <a
          href={directionsURL}
          target='_blank'
          rel='noreferrer noopener'
          onClick={() =>
            tracker.track(TrackingEvents.GOOGLE_MAPS_LINK_CLICKED, {
              context: 'profile_link',
              restaurant_guid: restaurantGuid
            })
          }
        >
          {t('location.directions')}
        </a>
      </p>
      {embeddedMapURL && (
        <iframe
          title={t('location.directions')}
          width='100%'
          height='300'
          loading='lazy'
          allowFullScreen
          src={embeddedMapURL}
          className='location-img'
        />
      )}
    </div>
  )
}

export default Location
